import {
  EstadosAgendamientoEnumChoice,
  EstadosLlamadaEnumChoice,
  ROUTER_PATHS,
  TrazabilidadAccionChoiceEnum,
} from "@/shared";
import {
  AproveRecoordinacionData,
  CreateAgendamientoParamsForm,
  useUpdateAgendamiento,
} from "@/shared/actions/erp";
import {
  CustomButtunsRejectConfirm,
  SingleFormBoxScene,
} from "@/shared/components/ui";
import {
  Agendamiento,
  AlertSeverityEnum,
  CacheKeyCalendarioENUM,
  EstadoBitacoraEnum,
  IDataPreventa,
  IDataProspecto,
} from "@/shared/interfaces";
import {
  calcTrazavilidadVendedor,
  convertirFormatoHora,
  convertirFormatoHoraArray,
  convertirHoraADecimalArray,
  handleErrorForm,
  hasPermission,
  preagendamientoFormSchema,
} from "@/shared/utils";
import { useUiStore } from "@/store/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
// import IServiceAgendaRecoordinadaFormTab from './IServiceAgendaRecoordinadaFormTab';
// import RejectAgendaModal from './RejectAgendaModal';
import { useFlotasStore } from "@/store/erp";
import { useDiccionarioMotivo } from "@/store/erp/diccionario_motivo/diccionario_motivo.store";
import useCalendarioInstalacionPyl from "../../store/useCalendarioInstalacionPyl.store";
import AgendamientoSinglePage from "../agendamiento_single_page/AgendamientoSinglePage";
import RejectRecoordinadoModal from "./RejectRecoordinadoModal";
import { PermissionsEnum } from "@/auth/interfaces";

export interface SaveRecoordinacionPyLProps {
  title: string;
  agendamiento?: Agendamiento;
  enableGestionVenta?: boolean
}

// PERF: la base era CreateAgendamientoParamsForm & Partial<IDataPreventa>(139 props) & Partial<IDataProspecto>,
// intersección enorme que hacía explotar Path<T> en cada useForm<SaveFormDataRecoordinacion> (tsc de 16-33 min).
// Se reemplaza la base por Record<string,any> (permisivo); se mantienen tipados los campos explícitos de abajo
// que el form realmente usa. Mismo comportamiento en runtime.
export type SaveFormDataRecoordinacion = Record<string, any> & {
    planLabel?: string;
    velocidad_internet?: string;
    valor_instalacion?: string;

    // prospecto
    puntaje_score: string;
    puntaje_empresa: string;
    puntaje_score_sobreendeudamiento: string;

    // preventa
    provincia: string;
    ciudad: string;
    zona: string;
    sector: string;
    observacion_1: string;
    observacion_2: string;

    // preagenda/agenda
    observacion_llamada: string;
    es_referido?: string;
    flotaRaw?: string;

    isRejecting?: boolean;
    observacion_rechazo?: string;
    id_preventa?: string;
    cantidad_reversos?: number;
  };

//export type SaveFormDataRecoordinacion = any;

const SaveRecoordinacionPyL: React.FC<SaveRecoordinacionPyLProps> = ({
  title,
  agendamiento,
  enableGestionVenta = hasPermission(
    PermissionsEnum.pyl_administrator_permission
  ),
}) => {
  const navigate = useNavigate();
  const returnUrlAgendamientosPyLPage =
    ROUTER_PATHS.operaciones.agendamientoPyLNav;

  ///* local state --------------------
  const [shrink] = useState(true);
  const setAlert = useUiStore((s) => s.setAlert);
  const [openRechazoModal, setOpenRechazoModal] = useState(false);

  ///* global state --------------------
  const {
    bloquear,
    fechaSelecionada,
    clickedIdHora,
    codigoFlota,
    horasSelect,
    setCountdownTimeOut,
    handleVerificarCache,
    setFechaHoraDisponible,
    setCodigoFlota,
    handleValidarFechaVendedor,
  } = useCalendarioInstalacionPyl();

  const emitFinalizarVenta = useDiccionarioMotivo((s) => s.emitFinalizarVenta);

  const { getFlotasPreagendamiento, isLoadingFlotasPreagendamiento } =
    useFlotasStore();

  ///* form --------------------
  const form = useForm<SaveFormDataRecoordinacion>({
    resolver: yupResolver(preagendamientoFormSchema) as any,
    defaultValues: {
      estado: true,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = form;

  ///* mutations --------------------
  const updatePreagendamientoMutation =
    useUpdateAgendamiento<AproveRecoordinacionData>({});

  ///* handlers --------------------
  const onSave = async (data: SaveFormDataRecoordinacion) => {
    if (!isValid) return;

    if (
      data.estado_llamada === EstadosLlamadaEnumChoice.REALIZADA &&
      !data.isRejecting
    ) {
      const esValidoHoraVendedor = await handleValidarFechaVendedor(
        agendamiento?.fecha_instalacion!,
        agendamiento?.hora_instalacion!,
        agendamiento?.flota!
      );

      if (!esValidoHoraVendedor) return;
    }

    ///* upd
    if (agendamiento?.id_agendamiento) {
      const resp = await updatePreagendamientoMutation.mutateAsync({
        id: agendamiento.id_agendamiento!,
        data: {
          celular: data.celular!,
          celular_2: data.celular_2!,
          email: data.email!,
          direccion: data.direccion!,
          coordenadas: data.coordenadas!,
          estado_llamada: data.estado_llamada!,
          fecha_hora_llamada: data.fecha_hora_llamada!,
          nap: data.nap,
          distancia: data.distancia,

          observaciones: {
            ...agendamiento?.preventa_data?.observaciones,
            observacion_llamada: data.observacion_llamada!,
            observacion_rechazo: data.observacion_rechazo || "",
          },
          pais: data.pais!,
          ciudad: data.ciudad!,
          provincia: data.provincia!,
          zona: data.zona!,
          sector: data.sector!,
          persona_referencia_celular: data.persona_referencia_celular!,
          persona_referencia_nombre: data.persona_referencia_nombre!,
          persona_referencia_parentesco: data.persona_referencia_parentesco!,

          estado_agendamiento: form.getValues().isRejecting
            ? EstadosAgendamientoEnumChoice.PRERECHAZADO
            : EstadosAgendamientoEnumChoice.RECORDINADO,
          motivo_rechazo: data.motivo_rechazo || null,
          observacion_rechazo: data.observacion_rechazo || null,
          id_preventa: data?.preventa_data?.id_preventa,

          ///* Cambio de flota y hora de instalacion
          flota: bloquear ? codigoFlota! : data.flota,
          fecha_instalacion: bloquear
            ? fechaSelecionada!
            : data.fecha_instalacion,
          hora_instalacion: bloquear
            ? convertirFormatoHora(+clickedIdHora!.replace("id-", ""))
            : data.hora_instalacion!,
          horas: convertirFormatoHoraArray(
            bloquear
              ? horasSelect
              : convertirHoraADecimalArray(data.hora_instalacion)
          ),
          fecha_instalacion_original:
            agendamiento.preagendamiento_data?.fecha_instalacion!,
          hora_instalacion_original:
            agendamiento.preagendamiento_data?.hora_instalacion!,
        } as any,
      });

      if (resp.code == 200) {
        emitFinalizarVenta({
          numero_referencia_venta:
            agendamiento.preagendamiento_data?.id_preagendamiento!,
          estado_venta: EstadoBitacoraEnum.FINALIZADO,
        });

        setCountdownTimeOut(
          0,
          CacheKeyCalendarioENUM.KEY_CACHE_PREAGENDAMIENTO_PYL,
          false,
          false
        );
        navigate(returnUrlAgendamientosPyLPage);
      }

      return;
    }
  };

  ///* effects --------------------
  useEffect(() => {
    if (!agendamiento?.id_agendamiento) return;

    const {
      preventa_data,
      prospecto_data,
      pais_data,
      provincia_data,
      ciudad_data,
      zona_data,
      sector_data,
      plan_data,
      flota_data,
      trazo_venta_data,
      ...rest
    } = agendamiento;

    reset({
      ...prospecto_data,
      ...preventa_data,
      ...rest,
      nacionalidad: pais_data?.nacionalidad!,
      observacion_1: preventa_data?.observaciones.observacion_1 || "",
      observacion_2: preventa_data?.observaciones.observacion_2 || "",

      vendedor_name: calcTrazavilidadVendedor(
        trazo_venta_data || [],
        TrazabilidadAccionChoiceEnum.CREACION,
        "user"
      ),
      es_referido: !!preventa_data?.tipo_referido ? "SI" : "NO",
      planLabel: plan_data?.nombre!,
      velocidad_internet: preventa_data?.velocidad
        ? `${preventa_data?.velocidad} ${plan_data?.unidad}`
        : "",

      isRejecting: false,

      // recoordinacion
      fecha_instalacion: agendamiento?.fecha_instalacion || "",
      hora_instalacion: agendamiento?.hora_instalacion || "",

      nap: agendamiento?.nap || "",
      distancia: agendamiento?.distancia || "",
      flota: agendamiento?.flota || "",
      flotaRaw: agendamiento?.flota_data || {},

      observacion_llamada:
        agendamiento.observaciones?.observacion_llamada || "",
      observacion_rechazo:
        agendamiento.observaciones?.observacion_rechazo || "",
      coordenadas: agendamiento.coordenadas,
    } as unknown as SaveFormDataRecoordinacion);
  }, [agendamiento, reset]);

  useEffect(() => {
    ///* Verificar si existe datos en cache, para la obtencion del tiempo del planificador
    getFlotasPreagendamiento({
      zonas: agendamiento?.preventa_data?.zona,
      page_size: 100,
    });

    if (!isLoadingFlotasPreagendamiento) {
      async function getCachePYL() {
        const resp = await handleVerificarCache(
          `${CacheKeyCalendarioENUM.KEY_CACHE_PREAGENDAMIENTO_PYL}${agendamiento?.preventa_data?.id_preventa}`,
          agendamiento?.preventa_data?.flota!,
          agendamiento?.preventa_data?.id_preventa!,
          CacheKeyCalendarioENUM.KEY_CACHE_PREAGENDAMIENTO_PYL,
          navigate
        );

        console.log(resp);

        if (resp?.codigo_flota) {
          form.reset({
            ...form.getValues(),
            flota: resp?.codigo_flota,
          });
        }
      }

      getCachePYL();
    }

    return () => {
      setTimeout(() => {
        ///* TODO: Soluccion temporal
        setCountdownTimeOut(
          0,
          CacheKeyCalendarioENUM.KEY_CACHE_PREAGENDAMIENTO_PYL,
          true,
          false
        );
        setFechaHoraDisponible(null);
        setCodigoFlota(null);
      }, 1000);
    };
  }, []);

  return (
    <SingleFormBoxScene
      titlePage={title}
      showCustomBtns={true}
      showActionBtns={false}
      num_referencia={`${agendamiento?.prospecto_data?.razon_social} — L${agendamiento?.prospecto_data?.linea}`}
      /* onSave={form.handleSubmit(onSave, (err) => {
        if (err?.estado_llamada || err?.observacion_llamada)
          return setAlert(
            true,
            "El estado de llamada y su observación son requeridos",
            AlertSeverityEnum.error
          );

        if (err?.fecha_instalacion)
          return setAlert(
            true,
            "La fecha de instalación es requerida",
            AlertSeverityEnum.error
          );
        if (err?.hora_instalacion)
          return setAlert(
            true,
            "La hora de instalación es requerida",
            AlertSeverityEnum.error
          );

        handleErrorForm(err);
      })} */
      customBtns={
        <CustomButtunsRejectConfirm
          onCancel={() => navigate(returnUrlAgendamientosPyLPage)}
          onConfirm={() => {
            form.handleSubmit(onSave, (err) => {
              if (err?.estado_llamada || err?.observacion_llamada)
                return setAlert(
                  true,
                  "El estado de llamada y su observación son requeridos",
                  AlertSeverityEnum.error
                );

              handleErrorForm(err);

              /* setAlert(
                true,
                'Faltan campos por completar',
                AlertSeverityEnum.error
              ); */
            })();
          }}
          rejectTextBtn="Enviar a Gestión Ventas"
          showRejectBtn={enableGestionVenta}
          onReject={() => {
            setOpenRechazoModal(true);
            form.reset({
              ...form.getValues(),
              isRejecting: true,
            });
          }}
          sxContainer={{
            pt: 1,
          }}
        />
      }
    >
      <AgendamientoSinglePage
        form={form as any}
        shrink={shrink}
        agendamiento={agendamiento!}
      />

      {/* =================== DIALOG =================== */}
      <RejectRecoordinadoModal
        open={openRechazoModal}
        onClose={() => {
          setOpenRechazoModal(false);
        }}
        agendamiento={agendamiento!}
        form={form}
        onConfirm={() => {
          handleSubmit(onSave, (err) => {
            handleErrorForm(err);
          })();
        }}
      />
    </SingleFormBoxScene>
  );
};

export default SaveRecoordinacionPyL;
